/* eslint-disable consistent-return, default-case, no-param-reassign */
/*
 *
 * Payoff reducer
 *
 */

import produce from 'immer';

import { RESET_SWITCH_LOAN_STATE, RESET_USER_STATE } from '../Main/constants';
import {
  CHANGE_PAYOFF_STEP,
  CLEAR_STATEMENT,
  CREATE_PAYOFF_STATEMENT,
  CREATE_PAYOFF_STATEMENT_FAILURE,
  CREATE_PAYOFF_STATEMENT_SUCCESS,
  FETCH_PAYOFF_DATES,
  FETCH_PAYOFF_DATES_FAILURE,
  FETCH_PAYOFF_DATES_SUCCESS,
  FETCH_PAYOFF_DOCUMENT,
  FETCH_PAYOFF_DOCUMENT_FAILURE,
  FETCH_PAYOFF_DOCUMENT_SUCCESS,
  FETCH_PAYOFF_HISTORY,
  FETCH_PAYOFF_HISTORY_FAILURE,
  FETCH_PAYOFF_HISTORY_SUCCESS,
  FETCH_PAYOFF_STATEMENT,
  FETCH_PAYOFF_STATEMENT_FAILURE,
  FETCH_PAYOFF_STATEMENT_SUCCESS,
  FETCH_STATEMENT_ERROR_MESSAGE,
  IS_INELIGIBLE,
  IS_MANCODE_Y,
  PAYOFF_DATES_BANNER_ERROR,
  RESET_BANNER_ERROR,
  RESET_STATE,
  SET_FILE,
  TOGGLE_DRAWER,
  UPDATE_REQUEST_TYPE,
  UPDATE_SELECTED_DATE,
  UPDATE_SELECTED_PAYOFF_REASON,
} from './constants';

export const initialState = {
  availablePayDownDates: [],
  availablePayOffDates: [],
  availablePayOffReasons: [],
  errors: {
    bannerError: {
      payoffDates: false,
      payoffDocument: false,
    },
    serviceError: {
      init: false,
      payoffDocument: false,
      payoffHistory: false,
      statement: false,
    },
  },
  file: {},
  isDrawerOpen: false,
  isIneligible: false,
  isLoanMancodeY: false,
  loading: {
    createStatement: false,
    fetchHistoryDocument: false,
    fetchStatement: false,
    init: true,
    payoffHistory: true,
  },
  needSpecialist: false,
  payoffHistoryData: [],
  payoffStep: 0,
  requestType: '',
  selectedDate: '',
  selectedPayOffReason: '',
  statement: null,
  status: { createStatement: 'idle' },
};

const payoffReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case CHANGE_PAYOFF_STEP: {
      const { payoffStep } = payload;
      draft.payoffStep = payoffStep;
      break;
    }
    case CLEAR_STATEMENT: {
      draft.selectedDate = initialState.selectedDate;
      draft.statement = initialState.statement;
      draft.status.createStatement = 'idle';
      break;
    }
    case CREATE_PAYOFF_STATEMENT: {
      draft.errors.serviceError.statement = initialState.errors.serviceError.statement;
      draft.loading.createStatement = true;
      draft.status.createStatement = 'loading';
      break;
    }
    case CREATE_PAYOFF_STATEMENT_FAILURE: {
      draft.errors.serviceError.statement = FETCH_STATEMENT_ERROR_MESSAGE;
      draft.loading.createStatement = false;
      draft.status.createStatement = 'failed';
      break;
    }
    case CREATE_PAYOFF_STATEMENT_SUCCESS: {
      draft.loading.createStatement = false;
      draft.status.createStatement = 'succeeded';
      break;
    }
    case FETCH_PAYOFF_DATES: {
      draft.errors.serviceError.init = false;
      draft.loading.init = true;
      /*
        The following shouldn't really need to be reset, since if they had been switched to true,
        the user wouldn't even be able to fetchPayoffDates again. But leaving them here just in
        case and for parity with what's getting set in the FAILURE case.
      */
      draft.errors.bannerError.payoffDates = false;
      draft.isIneligible = false;
      draft.isLoanMancodeY = false;
      break;
    }
    case FETCH_PAYOFF_DATES_FAILURE: {
      const { partialError } = payload;
      if (partialError === IS_INELIGIBLE) {
        draft.isIneligible = true;
      } else if (partialError === PAYOFF_DATES_BANNER_ERROR) {
        draft.errors.bannerError.payoffDates = partialError;
      } else if (partialError === IS_MANCODE_Y) {
        draft.isLoanMancodeY = true;
      } else {
        draft.errors.serviceError.init = true;
      }
      draft.loading.init = false;
      break;
    }
    case FETCH_PAYOFF_DATES_SUCCESS: {
      const {
        availablePayDownDates,
        availablePayOffDates,
        availablePayOffReasons,
      } = payload;

      if (availablePayOffDates) {
        draft.availablePayOffDates = availablePayOffDates;
      }
      if (availablePayDownDates) {
        draft.availablePayDownDates = availablePayDownDates;
      }
      if (availablePayOffReasons) {
        draft.availablePayOffReasons = availablePayOffReasons;
      }
      draft.loading.init = false;
      break;
    }
    case FETCH_PAYOFF_STATEMENT: {
      draft.loading.fetchStatement = true;
      break;
    }
    case FETCH_PAYOFF_STATEMENT_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.statement = error;
      draft.loading.fetchStatement = false;
      draft.status.createStatement = 'idle';
      break;
    }
    case FETCH_PAYOFF_STATEMENT_SUCCESS: {
      const {
        document,
        mobileDocument,
        status,
      } = payload;
      draft.loading.fetchStatement = false;
      draft.needSpecialist = status;
      draft.statement = { document, mobileDocument };
      break;
    }
    case FETCH_PAYOFF_HISTORY: {
      draft.errors = initialState.errors;
      draft.loading.payoffHistory = true;
      break;
    }
    case FETCH_PAYOFF_HISTORY_SUCCESS: {
      const { items } = payload;
      draft.payoffHistoryData = items;
      draft.loading.payoffHistory = false;
      break;
    }
    case FETCH_PAYOFF_HISTORY_FAILURE: {
      draft.loading.payoffHistory = false;
      draft.errors.serviceError.payoffHistory = true;
      break;
    }
    case FETCH_PAYOFF_DOCUMENT: {
      draft.loading.fetchHistoryDocument = true;
      draft.errors.bannerError.payoffDocument = false;
      break;
    }
    case FETCH_PAYOFF_DOCUMENT_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError.payoffDocument = error;
      draft.loading.fetchHistoryDocument = false;
      break;
    }
    case FETCH_PAYOFF_DOCUMENT_SUCCESS: {
      draft.loading.fetchHistoryDocument = false;
      break;
    }
    case RESET_BANNER_ERROR: {
      draft.errors.bannerError = initialState.errors.bannerError;
      break;
    }
    case RESET_STATE: {
      return initialState;
    }
    case RESET_SWITCH_LOAN_STATE: {
      return initialState;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case SET_FILE: {
      const { fileName, uri } = payload || {};
      if (fileName && uri) {
        draft.file = { fileName, uri };
      } else {
        draft.file = initialState.file;
      }
      break;
    }
    case TOGGLE_DRAWER: {
      const { drawerState } = payload;
      draft.isDrawerOpen = drawerState;
      break;
    }
    case UPDATE_REQUEST_TYPE: {
      const { requestType } = payload;
      draft.requestType = requestType;
      break;
    }
    case UPDATE_SELECTED_DATE: {
      const { date } = payload;
      draft.selectedDate = date;
      break;
    }
    case UPDATE_SELECTED_PAYOFF_REASON: {
      const { reason } = payload;
      draft.selectedPayOffReason = reason;
      break;
    }
  }
}, initialState);

export default payoffReducer;
